import { useEffect } from 'react'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'

export default function PrivacyPolicy() {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-slate-900">
            <Header />

            {/* Letter Container */}
            <div className="max-w-4xl mx-auto px-4 py-12">
                {/* Paper-like Document */}
                <div className="bg-white dark:bg-slate-800 shadow-2xl" style={{
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 10px 50px rgba(0,0,0,0.15)'
                }}>

                    {/* Letterhead */}
                    <div className="border-b-4 border-purple-600 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 px-12 py-8">
                        <div className="text-center">
                            <h1 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">
                                SUDHA'S ACADEMY
                            </h1>
                            <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                Online Learning Platform for Makeup & Beauty Education
                            </p>
                        </div>
                    </div>

                    {/* Document Body */}
                    <div className="px-12 py-10">

                        {/* Document Title */}
                        <div className="text-center mb-8 pb-6 border-b-2 border-gray-200 dark:border-gray-700">
                            <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-white mb-2">
                                PRIVACY POLICY
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Effective Date: December 5, 2025
                            </p>
                        </div>

                        {/* Letter Content */}
                        <div className="prose prose-sm max-w-none dark:prose-invert">

                            {/* Introduction */}
                            <div className="mb-8">
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    <strong>Welcome to Sudha's Academy!</strong>
                                </p>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    Sudha's Academy ("we", "us", or "our") respects your privacy and is committed to protecting the personal information of our students and visitors. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you access our website and mobile application (the "Platform") to participate in our beauty, makeup, and cosmetology courses.
                                </p>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    By accessing or using our Platform, you agree to the terms of this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access the Platform.
                                </p>
                            </div>

                            {/* Section 1 */}
                            <div className="mb-8">
                                <h3 className="text-lg font-serif font-bold text-gray-900 dark:text-white mb-4 uppercase">
                                    1. Information We Collect
                                </h3>

                                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                    1.1 Data You Provide to Us
                                </h4>
                                <ul className="list-disc pl-5 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                                    <li><strong>Account Data:</strong> When you register, we collect your name, email address, phone number, and password.</li>
                                    <li><strong>Profile Data:</strong> You may choose to provide a profile photo, bio, and other details.</li>
                                    <li><strong>Course Data:</strong> We collect assignments, practice photos/videos of your makeup work, and quiz responses you submit.</li>
                                    <li><strong>Payment Data:</strong> We collect data necessary to process your payment if you make purchases, such as your payment instrument number (handled securely by our payment processor, Razorpay).</li>
                                </ul>

                                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                    1.2 Data We Collect Automatically
                                </h4>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    We automatically collect certain data when you interact with the Platform, including:
                                </p>
                                <ul className="list-disc pl-5 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                                    <li><strong>System Data:</strong> IP address, device type, operating system version, and browser type.</li>
                                    <li><strong>Usage Data:</strong> Statistics on your interactions with the Platform, such as courses accessed, time spent on pages, pages visited, features used, and search queries.</li>
                                </ul>
                            </div>

                            {/* Section 2 */}
                            <div className="mb-8">
                                <h3 className="text-lg font-serif font-bold text-gray-900 dark:text-white mb-4 uppercase">
                                    2. How We Use Your Data
                                </h3>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    We use your data to provide, support, and personalize our Platform. Specifically, we use it to:
                                </p>
                                <ul className="list-disc pl-5 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                                    <li><strong>Provide Services:</strong> Process your enrollment, give you access to course content, issue certificates upon completion, and manage your account.</li>
                                    <li><strong>Communicate with You:</strong> Respond to your questions, send you administrative messages and information, including messages from instructors and teaching assistants, and send notifications about your course progress.</li>
                                    <li><strong>Improve Our Platform:</strong> Analyze trends and traffic, track purchases and usage data, and improve the quality of our courses and platform features.</li>
                                    <li><strong>Safety and Security:</strong> Detect, prevent, and address fraud and abuse, and ensure the security of our Platform.</li>
                                </ul>
                            </div>

                            {/* Section 3 */}
                            <div className="mb-8">
                                <h3 className="text-lg font-serif font-bold text-gray-900 dark:text-white mb-4 uppercase">
                                    3. Who We Share Your Data With
                                </h3>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    We do not sell your personal data. We share data with third parties only under the following circumstances:
                                </p>
                                <ul className="list-disc pl-5 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                                    <li><strong>Service Providers:</strong> We share data with third-party companies who perform services on our behalf, such as payment processing (Razorpay), data analysis, email delivery, hosting services, and customer service.</li>
                                    <li><strong>Instructors:</strong> We share data with instructors for courses you enroll in or request information about, so they can improve their courses and grade your assignments.</li>
                                    <li><strong>Legal Compliance:</strong> We may disclose your information if required to do so by law or in the good-faith belief that such action is necessary to comply with state and federal laws or respond to a court order, judicial, or other government subpoena or warrant.</li>
                                </ul>
                            </div>

                            {/* Section 4 */}
                            <div className="mb-8">
                                <h3 className="text-lg font-serif font-bold text-gray-900 dark:text-white mb-4 uppercase">
                                    4. Security
                                </h3>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    We use appropriate security based on the type and sensitivity of data being stored. As with any internet-enabled system, there is always a risk of unauthorized access, so it’s important to protect your password and to contact us if you suspect any unauthorized access to your account.
                                </p>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    We use industry-standard encryption (SSL/TLS) to protect your data during transmission and secure storage solutions for your personal information.
                                </p>
                            </div>

                            {/* Section 5 */}
                            <div className="mb-8">
                                <h3 className="text-lg font-serif font-bold text-gray-900 dark:text-white mb-4 uppercase">
                                    5. Your Rights
                                </h3>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    You have certain rights around the use of your data, including the ability to opt out of promotional emails, cookies, and collection of your data by certain analytics providers. You can update or terminate your account from within our Platform, and can also contact us for individual rights requests about your personal data.
                                </p>
                            </div>

                            {/* Section 6 */}
                            <div className="mb-8">
                                <h3 className="text-lg font-serif font-bold text-gray-900 dark:text-white mb-4 uppercase">
                                    6. Specific Policies for Beauty Academy
                                </h3>
                                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                    6.1 Student Practice Submissions
                                </h4>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    As part of your coursework, you may upload photos or videos of your makeup and beauty practice work. By uploading these, you grant Sudha's Academy a license to use these materials for educational assessment and, with your explicit permission, for showcasing student work on our platform or social media channels.
                                </p>
                                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                    6.2 Live Sessions
                                </h4>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    Live sessions may be recorded for quality assurance and for students who missed the session to view later. By participating in a live session, you consent to being recorded.
                                </p>
                            </div>

                            {/* Section 7 */}
                            <div className="mb-8">
                                <h3 className="text-lg font-serif font-bold text-gray-900 dark:text-white mb-4 uppercase">
                                    7. Updates & Contact Info
                                </h3>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-justify">
                                    When we make a material change to this policy, we’ll notify you via email or a prominent notice on our Platform.
                                </p>
                                <div className="bg-gray-50 dark:bg-slate-700 p-4 rounded border-l-4 border-purple-600 mb-6">
                                    <p className="text-gray-700 dark:text-gray-300 mb-1">
                                        <strong>Email:</strong> hello@sudhasacademy.com
                                    </p>
                                    <p className="text-gray-700 dark:text-gray-300 mb-1">
                                        <strong>Address:</strong> 13, Shermin Shahib St, Kamatchi Amman, Tiruppur, Tamil Nadu 641604
                                    </p>
                                </div>
                            </div>

                            {/* Closing */}
                            <div className="mb-8 pt-6 border-t-2 border-gray-200 dark:border-gray-700">
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                                    Sincerely,
                                </p>
                                <p className="text-gray-900 dark:text-white font-semibold text-lg">
                                    The Sudha's Academy Team
                                </p>
                            </div>

                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t-2 border-gray-200 dark:border-gray-700 px-12 py-6 bg-gray-50 dark:bg-slate-900">
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                            This document is a legally binding agreement between you and Sudha's Academy.
                            Please read it carefully and keep a copy for your records.
                        </p>
                    </div>

                </div>
            </div>

            <Footer />
        </div>
    )
}
